/* ================================================================
   K-NIN CONCEPT STORE — Enrichissement visuel (animations)
   assets/knin-motion.js

   Fichier isolé et désactivable : il suffit de retirer son
   chargement dans layout/theme.liquid (avec knin-motion.css)
   pour tout désactiver sans toucher au reste du thème.

   Ne modifie aucune logique existante (panier, header, produit) :
   observe le DOM et ajoute uniquement des classes/variables CSS.
   Respecte prefers-reduced-motion (désactivation quasi totale).
   ================================================================ */

(function () {
  'use strict';

  var prefersReduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canHoverFine = window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* Active les styles de base (reveal, parallaxe, etc.) */
  document.documentElement.classList.add('knin-motion-ready');

  /* Active les styles nécessitant un curseur précis (tilt, magnétique) */
  if (canHoverFine && !prefersReduced) {
    document.documentElement.classList.add('knin-motion-tilt-ready');
  }

  /* Portée des écouteurs de survol (tilt/magnétique) : uniquement le
     contenu principal, jamais le header ni le footer. Sans ça, un
     listener document-wide tourne aussi au-dessus des boutons du
     header à chaque mouvement de souris, pour rien. */
  var motionScope = document.getElementById('MainContent') || document;

  /* ── Petit utilitaire : throttle sur requestAnimationFrame ───── */
  function rafThrottle(fn) {
    var scheduled = false;
    var lastEvent;
    return function (e) {
      lastEvent = e;
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        fn(lastEvent);
      });
    };
  }

  /* ── Reveal générique au scroll (data-reveal) ─────────────────── */
  var revealEls = document.querySelectorAll('[data-reveal]');
  if (revealEls.length) {
    if (prefersReduced || !('IntersectionObserver' in window)) {
      revealEls.forEach(function (el) { el.classList.add('is-revealed'); });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
      revealEls.forEach(function (el) { io.observe(el); });
    }
  }

  /* ── Hero de page : reveal séquencé + parallaxe léger ─────────── */
  var heroes = document.querySelectorAll('.knin-page-hero');
  if (heroes.length && !prefersReduced) {
    heroes.forEach(function (hero) {
      hero.classList.add('knin-parallax');
      /* Double rAF : laisse le navigateur peindre l'état initial
         avant de déclencher la transition (sinon elle est ignorée) */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          hero.classList.add('knin-hero-in');
        });
      });
    });

    var updateParallax = rafThrottle(function () {
      var scrollY = window.scrollY || window.pageYOffset || 0;
      heroes.forEach(function (hero) {
        var rect = hero.getBoundingClientRect();
        /* Ne calcule que si le hero est visible ou proche */
        if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
        var y = Math.max(-40, Math.min(40, scrollY * 0.12));
        hero.style.setProperty('--knin-parallax-y', y + 'px');
      });
    });
    window.addEventListener('scroll', updateParallax, { passive: true });
    updateParallax();
  } else if (heroes.length) {
    /* Motion réduite : contenu visible immédiatement, pas de parallaxe */
    heroes.forEach(function (hero) { hero.classList.add('knin-hero-in'); });
  }

  /* ── Cartes produits : tilt 3D + zoom au survol (souris fine) ─── */
  if (canHoverFine && !prefersReduced) {
    var activeTiltCard = null;

    function resetTilt(card) {
      card.classList.remove('knin-tilt-active');
      card.style.setProperty('--knin-tilt-x', '0deg');
      card.style.setProperty('--knin-tilt-y', '0deg');
    }

    var handleTiltMove = rafThrottle(function (e) {
      var card = e.target.closest && e.target.closest('product-card');

      if (!card) {
        if (activeTiltCard) { resetTilt(activeTiltCard); activeTiltCard = null; }
        return;
      }
      if (card !== activeTiltCard) {
        if (activeTiltCard) resetTilt(activeTiltCard);
        activeTiltCard = card;
        card.classList.add('knin-tilt-active');
      }

      var rect = card.getBoundingClientRect();
      var px = (e.clientX - rect.left) / rect.width;
      var py = (e.clientY - rect.top) / rect.height;
      var rotY = (px - 0.5) * 10;
      var rotX = (0.5 - py) * 8;
      card.style.setProperty('--knin-tilt-x', rotX.toFixed(2) + 'deg');
      card.style.setProperty('--knin-tilt-y', rotY.toFixed(2) + 'deg');
    });

    motionScope.addEventListener('pointermove', handleTiltMove, { passive: true });
    motionScope.addEventListener('pointerleave', function () {
      if (activeTiltCard) { resetTilt(activeTiltCard); activeTiltCard = null; }
    }, true);
  }

  /* ── Boutons magnétiques (souris fine uniquement) ─────────────── */
  if (canHoverFine && !prefersReduced) {
    var MAG_SELECTOR = '.knin-btn-hero, .knin-coltabs__see-all';
    var MAG_STRENGTH = 0.28;
    var MAG_MAX = 12;
    var activeMagBtn = null;

    function resetMagnet(btn) {
      btn.classList.remove('knin-mag-active');
      btn.style.setProperty('--knin-mag-x', '0px');
      btn.style.setProperty('--knin-mag-y', '0px');
    }

    var handleMagMove = rafThrottle(function (e) {
      var btn = e.target.closest && e.target.closest(MAG_SELECTOR);

      if (!btn) {
        if (activeMagBtn) { resetMagnet(activeMagBtn); activeMagBtn = null; }
        return;
      }
      if (btn !== activeMagBtn) {
        if (activeMagBtn) resetMagnet(activeMagBtn);
        activeMagBtn = btn;
        btn.classList.add('knin-mag-active');
      }

      var rect = btn.getBoundingClientRect();
      var dx = (e.clientX - (rect.left + rect.width / 2)) * MAG_STRENGTH;
      var dy = (e.clientY - (rect.top + rect.height / 2)) * MAG_STRENGTH;
      dx = Math.max(-MAG_MAX, Math.min(MAG_MAX, dx));
      dy = Math.max(-MAG_MAX, Math.min(MAG_MAX, dy));
      btn.style.setProperty('--knin-mag-x', dx.toFixed(1) + 'px');
      btn.style.setProperty('--knin-mag-y', dy.toFixed(1) + 'px');
    });

    motionScope.addEventListener('pointermove', handleMagMove, { passive: true });
    motionScope.addEventListener('pointerleave', function () {
      if (activeMagBtn) { resetMagnet(activeMagBtn); activeMagBtn = null; }
    }, true);
  }

  /* ── Badge panier : petit "pop" quand la valeur change réellement ──
     Observe les badges déjà gérés par knin-header.js sans dépendre
     de sa logique interne (aucun couplage entre les deux fichiers). */
  if (!prefersReduced && 'MutationObserver' in window) {
    var cartBtns = document.querySelectorAll('.knin-cart-btn');

    function bumpBadge(el) {
      el.classList.remove('knin-cart-bump');
      // Force un reflow pour pouvoir relancer l'animation immédiatement
      void el.offsetWidth;
      el.classList.add('knin-cart-bump');
    }

    if (cartBtns.length) {
      var cartObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          var badge = null;

          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(function (node) {
              if (node.nodeType === 1 && node.classList && node.classList.contains('knin-cart-count')) {
                badge = node;
              }
            });
          } else if (mutation.type === 'characterData') {
            var parent = mutation.target.parentElement;
            if (parent && parent.classList && parent.classList.contains('knin-cart-count')) {
              badge = parent;
            }
          }

          if (badge) bumpBadge(badge);
        });
      });

      cartBtns.forEach(function (btn) {
        cartObserver.observe(btn, { childList: true, characterData: true, subtree: true });
      });
    }
  }

})();
